import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Loader } from 'lucide-react';

export default function MeetingEmbed({ meetingLink, roomConfig, userName, userEmail }) {
  const jitsiContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const jitsiApi = useRef(null);

  useEffect(() => {
    if (!meetingLink) {
      setError('No meeting link provided');
      setIsLoading(false);
      return;
    }

    // Load Jitsi Meet External API script
    const script = document.createElement('script');
    script.src = 'https://meet.jit.si/external_api.js';
    script.async = true;
    script.onload = () => initializeJitsi();
    script.onerror = () => {
      setError('Failed to load Jitsi Meet. Please check your internet connection.');
      setIsLoading(false);
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup: dispose of Jitsi API instance
      if (jitsiApi.current) {
        jitsiApi.current.dispose();
        jitsiApi.current = null;
      }

      // Remove script
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [meetingLink]);

  const initializeJitsi = () => {
    if (!jitsiContainerRef.current || !window.JitsiMeetExternalAPI) {
      setError('Failed to initialize Jitsi Meet');
      setIsLoading(false);
      return;
    }

    try {
      // Extract room name from meeting link
      const roomName = meetingLink.split('/').pop();
      const domain = meetingLink.split('//')[1].split('/')[0];

      // Configure Jitsi meeting - use backend config as primary source
      const options = {
        roomName: roomName,
        width: '100%',
        height: '100%',
        parentNode: jitsiContainerRef.current,
        configOverwrite: {
          ...roomConfig?.configOverwrite,
          // Ensure these critical settings are applied
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          enableWelcomePage: false,
          prejoinPageEnabled: false, // Disable prejoin to avoid lobby
          disableDeepLinking: true,
          enableLobbyChat: false,
          requireDisplayName: false,
          autoKnockLobby: false,
          enableInsecureRoomNameWarning: false,
          // Prevent breaking the layout
          defaultLocalDisplayName: userName || 'User',
          defaultRemoteDisplayName: 'Participant',
          // Keep meeting contained within the iframe
          startInIframe: true
        },
        interfaceConfigOverwrite: {
          ...roomConfig?.interfaceConfigOverwrite,
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false,
          BRAND_WATERMARK_LINK: '',
          SHOW_POWERED_BY: false,
          DISPLAY_WELCOME_PAGE_CONTENT: false,
          APP_NAME: 'Uzima Counselling',
          NATIVE_APP_NAME: 'Uzima Counselling',
          PROVIDER_NAME: 'Mount Kenya University',
          MOBILE_APP_PROMO: false
        },
        userInfo: {
          displayName: userName || 'User',
          email: userEmail || ''
        }
      };

      // Create Jitsi instance
      jitsiApi.current = new window.JitsiMeetExternalAPI(domain, options);

      // Event listeners
      jitsiApi.current.addEventListener('videoConferenceJoined', () => {
        console.log('User joined the meeting');
        setIsLoading(false);
      });

      jitsiApi.current.addEventListener('videoConferenceLeft', () => {
        console.log('User left the meeting');
      });

      jitsiApi.current.addEventListener('readyToClose', () => {
        console.log('Meeting is ready to close');
      });

      // Prevent Jitsi from entering browser fullscreen which would hide the notes panel
      jitsiApi.current.addEventListener('enterFullScreen', () => {
        console.log('Attempting to enter fullscreen - preventing to keep notes visible');
        // Exit fullscreen if it was triggered
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(err => console.log('Error exiting fullscreen:', err));
        }
      });

      // Set loading to false after a timeout if join event doesn't fire
      setTimeout(() => {
        setIsLoading(false);
      }, 5000);

    } catch (err) {
      console.error('Error initializing Jitsi:', err);
      setError('Failed to initialize meeting. Please try refreshing the page.');
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Meeting Error</h3>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full bg-gray-900">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
          <div className="text-center">
            <Loader className="h-12 w-12 text-white animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">Connecting to meeting...</p>
            <p className="text-gray-400 text-sm mt-2">Please wait while we set up your session</p>
          </div>
        </div>
      )}

      {/* Jitsi Container */}
      <div ref={jitsiContainerRef} className="h-full w-full" />

      {/* Meeting Info */}
      <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded-md text-sm">
        <p className="font-medium">Secure Counseling Session</p>
        <p className="text-xs text-gray-300 mt-1">End-to-end encrypted</p>
      </div>
    </div>
  );
}
